import React from 'react';
import { Text, TextStyle, StyleSheet, Platform, StyleProp } from 'react-native';
import { colors, typography, type TypographyKey } from '../../styles';
import { useTheme } from '../../context/ThemeContext';

type TypographyVariant = TypographyKey;

interface TypographyProps {
    children: React.ReactNode;
    variant?: TypographyVariant;
    color?: string;
    align?: 'left' | 'center' | 'right';
    style?: StyleProp<TextStyle>;
    numberOfLines?: number;
    adjustsFontSizeToFit?: boolean;
    minimumFontScale?: number;
    /** Heading level is explicit: a visual variant need not be a document heading. */
    headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
    accessibilityLiveRegion?: 'none' | 'polite' | 'assertive';
}

const DEFAULT_COLOR = colors.obsidian;

export const Typography: React.FC<TypographyProps> = ({
    children,
    variant = 'body',
    color = DEFAULT_COLOR,
    align = 'left',
    style,
    numberOfLines,
    adjustsFontSizeToFit,
    minimumFontScale,
    headingLevel,
    accessibilityLiveRegion,
}) => {
    const { colors: themeColors } = useTheme();

    const getCallerStyle = () => {
        if (Platform.OS !== 'web') return style;
        const flattened = StyleSheet.flatten(style) as TextStyle | undefined;
        if (!flattened) return flattened;
        // Web typography is role-driven. Callers may still control layout and
        // colour, but cannot silently invent another type scale or family.
        const {
            fontFamily: _fontFamily,
            fontSize: _fontSize,
            fontStyle: _fontStyle,
            fontWeight: _fontWeight,
            letterSpacing: _letterSpacing,
            lineHeight: _lineHeight,
            textTransform: _textTransform,
            ...safeStyle
        } = flattened;
        return safeStyle;
    };

    const getStyle = () => {
        const variantStyle = typography[variant];
        const baseStyle: TextStyle = {
            ...variantStyle,
            ...styles.base,
        };

        // Use theme-aware text color when caller uses the default
        const resolvedColor = color === DEFAULT_COLOR ? themeColors.textPrimary : color;
        if (resolvedColor) baseStyle.color = resolvedColor;
        if (align) baseStyle.textAlign = align;

        return [baseStyle, getCallerStyle()];
    };

    return (
        <Text
            accessibilityRole={headingLevel ? 'header' : undefined}
            {...(Platform.OS === 'web' && headingLevel ? { 'aria-level': headingLevel } : {})}
            accessibilityLiveRegion={accessibilityLiveRegion}
            style={getStyle() as StyleProp<TextStyle>}
            numberOfLines={numberOfLines}
            adjustsFontSizeToFit={adjustsFontSizeToFit}
            minimumFontScale={minimumFontScale}
        >
            {children}
        </Text>
    );
};

const styles = StyleSheet.create({
    base: {
        ...Platform.select({
            web: {
                userSelect: 'text',
            },
        }),
    },
});
